import { useEffect } from 'react';

interface SchemaMarkupProps {
  type: 'LocalBusiness' | 'Service' | 'FAQPage' | 'Product';
  data?: any;
}

export default function SchemaMarkup({ type, data }: SchemaMarkupProps) {
  useEffect(() => {
    const schemaId = `schema-${type}`;

    // Remove existing schema if present
    const existingSchema = document.getElementById(schemaId);
    if (existingSchema) {
      existingSchema.remove();
    }

    // Create appropriate schema based on type
    let schemaData;

    switch (type) {
      case 'LocalBusiness':
        schemaData = {
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Fuel Central",
          "description": "Professional gas station and convenience store market value estimation services using proprietary TAM methodology. Accurate market price estimates for station owners, brokers, and lenders. Not a substitute for formal appraisals.",
          "url": "https://fuelcentral.com",
          "logo": "https://fuelcentral.com/images/logo.png",
          "image": "https://fuelcentral.com/images/og-default.jpg",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "US"
          },
          "areaServed": {
            "@type": "Country",
            "name": "United States"
          },
          "serviceType": [
            "Gas Station Market Value Estimation",
            "Convenience Store Market Analysis",
            "Fuel Station Business Valuation",
            "Commercial Property Market Estimates"
          ],
          "knowsAbout": [
            "Gas Station Valuation",
            "TAM Estimation",
            "OpComps Analysis",
            "Fuel Station Benchmarking",
            "C-Store Performance Analysis"
          ]
        };
        break;

      case 'Service':
        schemaData = {
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Gas Station Valuation",
          "provider": {
            "@type": "ProfessionalService",
            "name": "Fuel Central"
          },
          "areaServed": {
            "@type": "Country",
            "name": "United States"
          },
          "description": "Professional gas station market value estimates using proprietary TAM (Transaction Adjusted Model) methodology. Comprehensive analysis including fuel volume projections, c-store performance, competitive positioning, and market comparables. Note: These are market price estimates, not formal appraisals.",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Gas Station Valuation Services",
            "itemListElement": [
              {
                "@type": "Service",
                "name": "TAM Estimation",
                "description": "Transaction Adjusted Model for accurate gas station valuations"
              },
              {
                "@type": "Service",
                "name": "OpComps Analysis",
                "description": "Operational comparables benchmarking against similar stations"
              },
              {
                "@type": "Service",
                "name": "Competitive Analysis",
                "description": "Location-based competitive positioning assessment"
              },
              {
                "@type": "Service",
                "name": "Market Benchmarking",
                "description": "Industry benchmark comparisons for fuel and c-store performance"
              }
            ]
          }
        };
        break;

      case 'FAQPage':
        schemaData = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data || []
        };
        break;

      case 'Product':
        schemaData = data;
        break;

      default:
        return;
    }

    // Add schema to head
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData, null, 2);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const element = document.getElementById(schemaId);
      if (element) {
        element.remove();
      }
    };
  }, [type, data]);

  return null;
}
